import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "@mui/material";
import useDeviceType from "../../../custom-hooks/DeviceType";
import WrapperCard from "../../../component-lib/Atoms/card/EventEHRWrapperCard";
import Heading from "../../../component-lib/Atoms/heading/EventEHRHeading";
import SearchSelectDropdown from "../../../component-lib/Atoms/SearchSelectDropdown/EventEHRSearchSelectDropdown";
import SelectDropdown from "../../../component-lib/Atoms/Select/Select";
import Table from "../../../component-lib/Organisms/table/EventEHRTable";
import Button from "../../../component-lib/Molecules/button/Button";
import MenuDropdown from "../../../component-lib/Molecules/menuDropdown/MenuDropdown";
import EventEHRConfirmationDialog from "../../../component-lib/Molecules/confimation-dialog/EventEHRConfirmationDialog";
import { plusIcon, rightDirectionBlackIcon, searchIcon } from "../../../assets";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";
import { componentKey, setCloseAddOrderTypeDialog } from "../SettingsSlice";
import {
  deleteOrderType,
  getOrderTypeList,
  patchOrderTypeData,
  postOrderTypeData,
} from "../SettingsSaga";
import OrderTypeDialog from "../dialogs/OrderTypeDialog";
import { getFacilityList } from "../../facility-management-screen/FacilityManagementSaga";
import { componentKey as facilityComponentKey } from "../../facility-management-screen/FacilityManagementSlice";

const OrderTypesScreen = () => {
  const { orderTypeList, closeAddOrderTypeDialog } = useSelector(
    (state: any) => state[componentKey],
  );
  const { facilityList } = useSelector(
    (state: any) => state[facilityComponentKey],
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const deviceType = useDeviceType();

  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [rowPerPage, setRowPerPage] = useState("10");
  const [pageNumber, setPageNumber] = useState(1);
  const [searchedValue, setSearchValue] = useState<string>("");
  const [selectedFacility, setSelectedFacility] = useState<string>("");
  const [dialogState, setDialogState] = useState<null | "add" | "edit">(null);
  const [defaultRow, setDefaultRow] = useState<any>({});
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [selectedOrderTypeId, setSelectedOrderTypeId] = useState<string | null>(
    null,
  );

  const facilityOptions: { label: string; value: string }[] = (
    facilityList?.facilitys || []
  ).map((f: any) => ({ label: f?.name, value: f?.id }));

  const columns = [
    {
      field: "name",
      label: <div>Name</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.name}</p>
      ),
    },
    {
      field: "isItemRequired",
      label: <div>Item Required</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">
          {row?.isItemRequired ? "Yes" : "No"}
        </p>
      ),
    },
    {
      field: "isNoteRequired",
      label: <div>Note Required</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">
          {row?.isNoteRequired ? "Yes" : "No"}
        </p>
      ),
    },
    {
      field: "facilities",
      label: <div>Facilities</div>,
      renderLogic: (row: any) => {
        const names: string[] = (row?.facilities || [])
          .map((f: any) => f?.facility?.name)
          .filter(Boolean);
        if (!names.length) {
          return <p className="text-neutral-90 font-normal">-</p>;
        }
        const [first, ...rest] = names;
        return (
          <Tooltip
            arrow
            placement="bottom-start"
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: "#FFFFFF",
                  color: "#1D1D1D",
                  boxShadow: "0px 4px 12px rgba(0,0,0,0.12)",
                  border: "1px solid #EAEAEA",
                  borderRadius: "8px",
                  padding: "10px",
                  maxWidth: 360,
                },
              },
              arrow: { sx: { color: "#FFFFFF" } },
            }}
            title={
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                }}
              >
                {names.map((n, i) => (
                  <span
                    key={`${n}-${i}`}
                    style={{
                      backgroundColor: "#E9E9E9",
                      color: "#005596",
                      fontWeight: 600,
                      fontSize: "13px",
                      padding: "4px 10px",
                      borderRadius: "12px",
                      width: "fit-content",
                    }}
                  >
                    {n}
                  </span>
                ))}
              </div>
            }
          >
            <p className="text-neutral-90 font-normal cursor-default">
              {first}
              {rest.length > 0 && (
                <span style={{ color: "#005596", marginLeft: 4 }}>
                  +{rest.length}
                </span>
              )}
            </p>
          </Tooltip>
        );
      },
    },
    {
      field: "action",
      label: <div>{TEXT_CONSTANTS?.ACTION}</div>,
      renderLogic: (row: any) => (
        <div className="flex items-center">
          <MenuDropdown
            options={[
              {
                label: "Edit",
                onClickCb: () => {
                  setDefaultRow(row);
                  setDialogState("edit");
                },
              },
              {
                label: "Delete",
                onClickCb: () => {
                  setSelectedOrderTypeId(row?.id);
                  setOpenConfirmationDialog(true);
                },
              },
            ]}
          />
        </div>
      ),
    },
  ];

  const rows = orderTypeList?.orderTypes?.map((data: any) => ({
    id: data?.id || "-",
    name: data?.name || "-",
    isItemRequired: !!data?.isItemRequired,
    isNoteRequired: !!data?.isNoteRequired,
    facilities: data?.facilities || [],
  }));

  const fetchOrderTypes = () => {
    dispatch(
      getOrderTypeList({
        pageNumber,
        limit: rowPerPage,
        searchValue: searchedValue?.trim(),
        facilityId: selectedFacility || undefined,
      }),
    );
  };

  useEffect(() => {
    fetchOrderTypes();
  }, [pageNumber, rowPerPage, searchedValue, selectedFacility]);

  useEffect(() => {
    dispatch(getFacilityList({ pageNumber: 1, limit: 1000 }));
  }, [dispatch]);

  useEffect(() => {
    if (closeAddOrderTypeDialog) {
      dispatch(setCloseAddOrderTypeDialog(false));
      setDialogState(null);
      setDefaultRow({});
      setOpenConfirmationDialog(false);
      setSelectedOrderTypeId(null);
      fetchOrderTypes();
    }
  }, [closeAddOrderTypeDialog]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageNumber(1);
    const value = e.target.value;
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(() => {
      setSearchValue(value?.trim());
    }, 500);
  };

  return (
    <WrapperCard
      customClasses="m-3 p-3 flex-1 min-h-0"
      customStyle={{ minWidth: deviceType !== "mobile" ? "400px" : "" }}
    >
      <div className="flex flex-col h-full gap-3">
        <div
          className={`flex ${deviceType === "mobile" || deviceType === "small-tablet" ? "flex-col items-start gap-3" : "items-center"} justify-between w-full`}
        >
          <div className="flex items-center gap-3">
            <img
              className="cursor-pointer"
              src={rightDirectionBlackIcon}
              alt="back-arrow"
              style={{ height: "12.5px" }}
              onClick={() => {
                navigate("/settings");
              }}
            />
            <Heading title={"Order Types"} customStyle={{ color: "#1D1D1D" }} />
          </div>

          <div
            className={`flex ${deviceType === "mobile" || deviceType === "small-tablet" ? "w-full justify-end" : ""} gap-3`}
          >
            <div style={{ minWidth: "180px" }}>
              <SelectDropdown
                displayEmpty
                placeholderValue="Select Facility"
                size="small"
                value={selectedFacility}
                options={[
                  { label: "All Facilities", value: "" },
                  ...facilityOptions,
                ]}
                onChangeCb={(e: any) => {
                  setSelectedFacility(e.target.value);
                  setPageNumber(1);
                }}
              />
            </div>
            <div>
              <SearchSelectDropdown
                freeSolo={true}
                searchedValue={searchedValue}
                onSearchValueChangeCb={(e: any) => handleSearchChange(e)}
                size="small"
                label="Search by name"
                fullWidth={deviceType === "mobile" ? "160px" : "300px"}
                startIcon={
                  <img
                    alt="search-icon"
                    src={searchIcon}
                    width={"18px"}
                    height={"18px"}
                  />
                }
              />
            </div>
            <Button
              startIcon={
                <img
                  src={plusIcon}
                  alt={"add"}
                  className="cursor-pointer w-[19px] h-[19px]"
                />
              }
              label="Add Order Type"
              variant="contained"
              onClickCb={() => {
                setDefaultRow({});
                setDialogState("add");
              }}
              style={{
                paddingTop: "6px",
                paddingBottom: "6px",
                minWidth: "155px",
              }}
            />
          </div>
        </div>

        <div style={{ overflow: "auto" }}>
          <Table
            columns={columns}
            rows={rows}
            paginationProps={{
              isPagination: true,
              page: pageNumber,
              totalPages: orderTypeList?.totalPages,
              totalCount: orderTypeList?.total,
              limit: rowPerPage,
              rowsPerPageOptions: [10, 25, 50],
              onPageChange: (page: any) => {
                setPageNumber(page);
              },
              handleChangeRowsPerPage: (rowPerPage: any) => {
                setRowPerPage(rowPerPage);
                setPageNumber(1);
              },
            }}
          />
        </div>
      </div>

      {dialogState && (
        <OrderTypeDialog
          isOpen={!!dialogState}
          isEdit={dialogState === "edit"}
          defaultOrderTypeData={defaultRow}
          handleClose={() => {
            setDialogState(null);
            setDefaultRow({});
          }}
          facilityOptions={facilityOptions}
          handleData={(data) => {
            const payload = { ...data, status: "Active" };
            if (dialogState === "edit" && defaultRow?.id) {
              dispatch(
                patchOrderTypeData({
                  orderTypeId: defaultRow.id,
                  payload,
                }),
              );
            } else {
              dispatch(postOrderTypeData(payload));
            }
          }}
        />
      )}

      {openConfirmationDialog && (
        <EventEHRConfirmationDialog
          alertTitle="Delete Order Type"
          alertDescription="Are you sure you want to delete this Order Type? Please confirm to proceed."
          cancelButtonTitle="Cancel"
          submitButtonTitle="Delete"
          open={openConfirmationDialog}
          handleClose={(action) => {
            if (action === "close") {
              setOpenConfirmationDialog(false);
              setSelectedOrderTypeId(null);
            } else if (action === "submit" && selectedOrderTypeId) {
              dispatch(deleteOrderType({ orderTypeId: selectedOrderTypeId }));
            }
          }}
        />
      )}
    </WrapperCard>
  );
};

export default OrderTypesScreen;
